// src/pages/ProviderProfilePage.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, ShieldCheck } from 'lucide-react'
import { atlasGeneric, type ProviderProfession } from '../api'
import { buildProviderLocation } from '../locationDisplay'
import { auth } from '../firebase'
import '../styles.css'

type SearchResult = {
  userId?: string
  name: string
  profession: string
  stack?: string | null
  specialties?: string[]
  specialty?: string | null
  displayProfession?: string | null
  title?: string | null
  profile?: string | null
  location?: string | null
  price?: string | null
  rating?: number | null
  ratingCount?: number | null
  pricingNotes?: string | null
  image?: string | null
  slug?: string
  availabilitySlots?: string[]
  countryCode?: string | null
  country?: string | null
}

const STACK_LABELS: Record<string, string> = {
  doctor: 'Medical Doctors',
  allied: 'Allied Health',
  education: 'Education & Tutoring',
  financial: 'Financial',
  migration: 'Migration & Visa',
  legal: 'Legal, Governance & Compliance',
  property: 'Property, Design & Building',
  tech: 'Tech, Creative & Media',
}

const STACK_API_BASES: Record<string, string | undefined> = {
  doctor: import.meta.env.VITE_DOCTOR_API_BASE || import.meta.env.VITE_API_DOCTOR_BASE || import.meta.env.VITE_API_BASE,
  allied: import.meta.env.VITE_ALLIED_API_BASE || import.meta.env.VITE_API_ALLIED_BASE || import.meta.env.VITE_API_BASE,
  education: import.meta.env.VITE_EDUCATION_API_BASE || import.meta.env.VITE_API_EDUCATION_BASE || import.meta.env.VITE_API_BASE,
  financial: import.meta.env.VITE_FINANCIAL_API_BASE || import.meta.env.VITE_API_FINANCIAL_BASE || import.meta.env.VITE_API_BASE,
  migration: import.meta.env.VITE_MIGRATION_API_BASE || import.meta.env.VITE_API_MIGRATION_BASE || import.meta.env.VITE_API_BASE,
  legal: import.meta.env.VITE_LEGAL_API_BASE || import.meta.env.VITE_API_LEGAL_BASE || import.meta.env.VITE_API_BASE,
  property: import.meta.env.VITE_PROPERTY_API_BASE || import.meta.env.VITE_API_PROPERTY_BASE || import.meta.env.VITE_API_BASE,
  tech: import.meta.env.VITE_TECH_API_BASE || import.meta.env.VITE_API_TECH_BASE || import.meta.env.VITE_API_BASE,
}

const SEARCH_PUBLIC_API = (
  import.meta.env.VITE_SEARCH_API_BASE ||
  import.meta.env.VITE_API_BASE ||
  'https://54uqjwvuke.execute-api.ap-southeast-2.amazonaws.com/prod'
).replace(/\/+$/, '')

const STACK_SHOWCASE_PATHS: Record<ProviderProfession, string> = {
  doctor: '/public/doctors/showcase',
  allied: '/public/allied/showcase',
  education: '/public/education/showcase',
  financial: '/public/financial/showcase',
  migration: '/public/migration/showcase',
  legal: '/public/legal/showcase',
  property: '/public/property/showcase',
  tech: '/public/tech/showcase',
}

const ALL_STACKS: ProviderProfession[] = [
  'doctor',
  'allied',
  'education',
  'financial',
  'migration',
  'legal',
  'property',
  'tech',
]

const LS_BACKEND_TOKEN = 'eucamd.backend.jwt'
const LICENSE_VERIFIED_TOOLTIP =
  'License Verified: This professional’s license has been verified by EUCA Match.'

const slugKey = (value?: string | null) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')

const fetchWithRetry = async (makeUrl: () => string, attempts = 2, delayMs = 250) => {
  let lastErr: any = null
  for (let i = 0; i < attempts; i += 1) {
    try {
      const res = await fetch(makeUrl(), { cache: 'no-store' })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) {
        throw new Error(json?.error || res.statusText || 'Request failed')
      }
      return json
    } catch (err: any) {
      lastErr = err
      if (i < attempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, delayMs * (i + 1)))
      }
    }
  }
  throw lastErr || new Error('Request failed')
}

const formatPriceLabel = (pricing: any) => {
  if (!pricing || typeof pricing !== 'object') return null
  const min = typeof pricing.minAud === 'number' ? pricing.minAud : typeof pricing.min === 'number' ? pricing.min : null
  const max = typeof pricing.maxAud === 'number' ? pricing.maxAud : typeof pricing.max === 'number' ? pricing.max : null
  const currencyCode =
    typeof pricing.currency === 'string' && pricing.currency.trim()
      ? pricing.currency.trim().toUpperCase()
      : 'AUD'
  if (min === 0) return 'Contact for quote'
  if (min != null && max != null && max >= min) return `$${min.toFixed(0)}–$${max.toFixed(0)} ${currencyCode}`
  if (min != null) return `From $${min.toFixed(0)} ${currencyCode}`
  return null
}

const mapSearchItem = (item: any): SearchResult => {
  const fallbackSlug = slugKey(item.slug || item.name || item.userId || 'profile') || 'profile'
  const locationData = buildProviderLocation(item)
  const displayProfession = item.displayProfession || null
  const rating =
    typeof item.rating === 'number'
      ? Number(item.rating)
      : typeof item.rating === 'string'
        ? Number(item.rating)
        : typeof item.ratingAverage === 'number'
          ? Number(item.ratingAverage)
          : typeof item.ratingAverage === 'string'
            ? Number(item.ratingAverage)
            : null
  const ratingCount =
    typeof item.ratingCount === 'number'
      ? item.ratingCount
      : typeof item.ratingCount === 'string'
        ? Number(item.ratingCount)
        : typeof item.reviewCount === 'number'
          ? item.reviewCount
          : null
  const priceLabel = item.priceLabel || formatPriceLabel(item.pricing) || item.price || null
  const image = item.image || item.photoPublicUrl || item.photoUrl || item.photo || null

  return {
    userId: item.userId,
    name: item.name || item.profession || 'Professional',
    profession: displayProfession || item.profession || 'Professional',
    displayProfession,
    stack: item.stack || null,
    specialties: Array.isArray(item.specialties) ? item.specialties : [],
    specialty: item.specialty || null,
    title: item.title || null,
    profile: item.profile || null,
    location: locationData.display,
    price: priceLabel,
    rating: Number.isFinite(rating) ? rating : null,
    ratingCount,
    pricingNotes: item.pricingNotes || item.pricing?.notes || null,
    image,
    slug: item.slug || fallbackSlug,
    availabilitySlots: Array.isArray(item.availabilitySlots) ? item.availabilitySlots : [],
    countryCode: locationData.countryCode,
    country: locationData.country,
  }
}

const fetchPublicSearch = async (query: string) => {
  const makeUrl = () => {
    const url = new URL(`${SEARCH_PUBLIC_API}/public/search`)
    if (query.trim()) url.searchParams.set('q', query.trim())
    url.searchParams.set('ts', Date.now().toString())
    return url.toString()
  }
  const json = await fetchWithRetry(makeUrl, 3, 300)
  return Array.isArray((json as any)?.items) ? (json as any).items : []
}

const fetchStackShowcase = async (profession: ProviderProfession) => {
  const apiBase = STACK_API_BASES[profession]
  const path = STACK_SHOWCASE_PATHS[profession]
  if (!apiBase || !path) return []

  const makeUrl = () => {
    const url = new URL(`${apiBase.replace(/\/+$/, '')}${path}`)
    url.searchParams.set('ts', Date.now().toString())
    return url.toString()
  }

  try {
    const json = await fetchWithRetry(makeUrl, 2, 250)
    const items = Array.isArray((json as any)?.items) ? (json as any).items : []
    return items.map((item: any) => ({ ...item, stack: profession }))
  } catch {
    return []
  }
}

const fetchAllShowcases = async () => {
  const results = await Promise.all(ALL_STACKS.map((stack) => fetchStackShowcase(stack)))
  return results.flat()
}

const fetchAuthToken = async (): Promise<string | null> => {
  const user = auth.currentUser
  if (user) {
    try {
      return await user.getIdToken()
    } catch {
      // fall through to backend token
    }
  }
  try {
    if (typeof window !== 'undefined') {
      const tok = window.localStorage.getItem(LS_BACKEND_TOKEN)
      if (tok) return tok
    }
  } catch {
    // ignore storage errors
  }
  return null
}

const buildAuthHeader = async () => {
  const token = await fetchAuthToken()
  if (!token) return {}
  const needsBearer = !token.startsWith('EUCA ')
  return { Authorization: needsBearer ? `Bearer ${token}` : token }
}

const mapAdminProfile = (profile: any, stack: ProviderProfession): SearchResult => {
  const name = profile?.name || profile?.practiceName || profile?.businessName || 'Professional'
  const displayProfession = profile?.displayProfession || null
  const locationData = buildProviderLocation({
    city: profile?.publicCity || profile?.city || profile?.address?.city,
    state: profile?.publicState || profile?.state || profile?.address?.state,
    countryCode: profile?.publicCountryCode || profile?.countryCode || profile?.address?.countryCode,
    country: profile?.publicCountry || profile?.country || profile?.address?.country,
    address: profile?.address,
  })
  const pricing = profile?.pricing || null
  const price =
    profile?.priceLabel ||
    formatPriceLabel(pricing) ||
    (typeof profile?.price === 'string' ? profile.price : null)
  const image =
    profile?.photoPublicUrl ||
    profile?.image ||
    profile?.photoUrl ||
    profile?.photo ||
    null
  const rating =
    typeof profile?.ratingAverage === 'number'
      ? profile.ratingAverage
      : typeof profile?.rating === 'number'
        ? profile.rating
        : null
  const ratingCount =
    typeof profile?.ratingCount === 'number'
      ? profile.ratingCount
      : typeof profile?.reviewCount === 'number'
        ? profile.reviewCount
        : null
  const slug = profile?.slug || slugKey(name || profile?.userId || 'profile')

  return {
    userId: profile?.userId,
    name,
    profession: displayProfession || profile?.profession || STACK_LABELS[stack] || 'Professional',
    stack,
    specialties: Array.isArray(profile?.specialties) ? profile.specialties : [],
    specialty: profile?.specialty || null,
    displayProfession,
    title: profile?.title || null,
    profile: profile?.profile || null,
    location: locationData.display,
    price: price || null,
    rating: typeof rating === 'number' ? rating : null,
    ratingCount,
    pricingNotes: profile?.pricingNotes || pricing?.notes || null,
    image,
    slug,
    availabilitySlots: Array.isArray(profile?.availabilitySlots) ? profile.availabilitySlots : [],
    countryCode: locationData.countryCode,
    country: locationData.country,
  }
}

const fetchAdminProviderProfile = async (
  stack: ProviderProfession,
  userId: string,
  authHeader: Record<string, string>
) => {
  const baseUrl = STACK_API_BASES[stack]
  if (!baseUrl) return null
  const url = `${baseUrl.replace(/\/+$/, '')}/admin/providers/${encodeURIComponent(userId)}?ts=${Date.now()}`
  const res = await fetch(url, { headers: authHeader, cache: 'no-store' })
  if (!res.ok) return null
  const json = await res.json().catch(() => ({}))
  return (json as any)?.profile || null
}

const findProviderViaAdmin = async (userId: string) => {
  if (!userId) return null
  const authHeader = await buildAuthHeader()
  if (!authHeader.Authorization) return null
  const attempts = await Promise.allSettled(
    ALL_STACKS.map(async (stack) => {
      const profile = await fetchAdminProviderProfile(stack, userId, authHeader)
      if (!profile?.userId) return null
      return mapAdminProfile(profile, stack)
    })
  )
  for (const attempt of attempts) {
    if (attempt.status === 'fulfilled' && attempt.value) return attempt.value
  }
  return null
}

const findProviderBySlug = async (slug: string) => {
  const targetKey = slugKey(slug)
  if (!targetKey) return null

  try {
    const publicItems = await fetchPublicSearch(slug)
    const mapped = publicItems.map(mapSearchItem)
    const match = mapped.find(
      (item) => slugKey(item.slug) === targetKey || slugKey(item.userId) === targetKey
    )
    if (match) return match
  } catch {
    // fall through to showcase fetch
  }

  const showcaseItems = (await fetchAllShowcases()).map(mapSearchItem)
  const showcaseMatch =
    showcaseItems.find(
      (item) => slugKey(item.slug) === targetKey || slugKey(item.userId) === targetKey
    ) || null
  if (showcaseMatch) return showcaseMatch

  // Final fallback for admin/internal use: treat slug as a userId and try admin endpoints.
  const adminCandidates = Array.from(
    new Set([slug, targetKey].map((val) => String(val || '').trim()).filter(Boolean))
  )
  for (const candidate of adminCandidates) {
    try {
      const found = await findProviderViaAdmin(candidate)
      if (found) return found
    } catch {
      // try next candidate
    }
  }
  return null
}

const advisoryLine =
  'Services provided are compliance and governance advisory only and do not constitute legal advice or legal representation.'
const mediatorAdvisoryLine =
  'Services provided are mediation and dispute resolution services only and do not constitute legal advice or legal representation.'

const escapeHtml = (str: string = '') =>
  str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const normalizeLine = (value: string = '') => value.toLowerCase().replace(/\s+/g, ' ').trim()
const normalizeSpecialty = (value: string = '') => value.toLowerCase().replace(/[^a-z0-9]/g, '')
const hasSpecialty = (specialties: string[] = [], target: string) => {
  const needle = normalizeSpecialty(target)
  return specialties.some((spec) => normalizeSpecialty(spec) === needle)
}
const ensureDisclaimer = (text: string, line: string) => {
  if (!line) return text
  const normalizedLine = normalizeLine(line)
  if (!normalizedLine) return text
  if (normalizeLine(text).includes(normalizedLine)) return text
  if (!text) return line
  return `${text}\n\n${line}`
}
const applyProfileDisclaimers = (text: string, specialties: string[] = []) => {
  let out = text
  if (hasSpecialty(specialties, 'Nationally Accredited Mediator')) {
    out = ensureDisclaimer(out, mediatorAdvisoryLine)
  }
  return out
}
const renderProfileHtml = (text: string) => {
  const escaped = escapeHtml(text)
  const highlightLines = [advisoryLine, mediatorAdvisoryLine]
  const withEmphasis = highlightLines.reduce((acc, line) => {
    if (!line) return acc
    const escapedLine = escapeHtml(line)
    return acc.replace(escapedLine, `<em><strong>${line}</strong></em>`)
  }, escaped)
  return withEmphasis.replace(/\n/g, '<br />')
}

const guessStack = (item?: Partial<SearchResult> | null): ProviderProfession => {
  if (item?.stack && STACK_LABELS[item.stack]) return item.stack as ProviderProfession
  const name = (item?.profession || '').toLowerCase()
  if (name.includes('doctor')) return 'doctor'
  if (name.includes('allied')) return 'allied'
  if (name.includes('visa') || name.includes('migration')) return 'migration'
  if (name.includes('legal') || name.includes('law')) return 'legal'
  if (name.includes('property') || name.includes('real')) return 'property'
  if (name.includes('tech') || name.includes('creative') || name.includes('it')) return 'tech'
  if (name.includes('finance') || name.includes('account')) return 'financial'
  if (name.includes('educat') || name.includes('tutor')) return 'education'
  return 'doctor'
}

const normalizeBadgeToken = (value: string) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

const hasLicenseVerifiedBadge = (item?: Partial<SearchResult> | null) => {
  const stack = guessStack(item || {})
  if (stack === 'doctor' || stack === 'allied' || stack === 'financial' || stack === 'migration') return true
  const tokens = [
    item?.profession,
    item?.displayProfession,
    item?.specialty,
    item?.title,
    ...(Array.isArray(item?.specialties) ? item.specialties : []),
  ]
    .filter(Boolean)
    .map((value) => normalizeBadgeToken(String(value)))
    .filter(Boolean)
  if (stack === 'legal') {
    if (tokens.some((token) => token.includes('principal lawyer'))) return true
    if (tokens.some((token) => token.includes('nationally accredited mediator'))) return true
    if (tokens.some((token) => token.includes('naati') && token.includes('translator'))) return true
    return false
  }
  if (stack === 'property') {
    if (tokens.some((token) => token.includes('forensic engineer'))) return true
    if (tokens.some((token) => token.includes('real estate agent'))) return true
    if (tokens.some((token) => /\barchitect\b/.test(token))) return true
    return false
  }
  if (stack === 'education') {
    const labelTokens = [
      item?.profession,
      item?.displayProfession,
      item?.specialty,
      item?.title,
    ]
      .filter(Boolean)
      .map((value) => normalizeBadgeToken(String(value)))
      .filter(Boolean)
    if (labelTokens.some((token) => token.includes('education advisor'))) return true
    if (labelTokens.some((token) => /\blecturer\b/.test(token))) return true
    if (labelTokens.some((token) => /\bteacher\b/.test(token))) return true
    return false
  }
  return false
}

const LicenseVerifiedBadge = ({ item }: { item?: Partial<SearchResult> | null }) => {
  if (!hasLicenseVerifiedBadge(item)) return null
  return (
    <span
      className="license-verified-badge"
      aria-label="License Verified"
      role="img"
      tabIndex={0}
      data-tooltip={LICENSE_VERIFIED_TOOLTIP}
    >
      <ShieldCheck size={13} aria-hidden />
    </span>
  )
}

const renderStars = (val: any) => {
  const n = Math.max(1, Math.min(5, Math.round(Number(val) || 0)))
  return Array.from({ length: n }).map((_, i) => (
    <span key={i} className="star" aria-hidden>
      ★
    </span>
  ))
}

const reviewCountLabel = (value?: string | number | null) => {
  const count = Number(value) || 0
  return `${count.toLocaleString()} ${count === 1 ? 'review' : 'reviews'}`
}

const shouldHideLocation = (item?: SearchResult | null) => {
  if (!item) return true
  const hiddenNames = ['paul ledgerwood']
  const name = (item?.name || '').toLowerCase()
  return hiddenNames.some((n) => name.includes(n))
}

const CONTACT_SLOT = 'REQUEST_BOOKING'

const parseReviewDate = (value?: string | null) => {
  if (!value) return null
  const trimmed = String(value).trim()
  if (!trimmed || trimmed === CONTACT_SLOT) return null
  if (/^\d+$/.test(trimmed)) {
    const raw = Number(trimmed)
    const ms = trimmed.length <= 10 ? raw * 1000 : raw
    const parsed = new Date(ms)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  const parsed = new Date(trimmed)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const formatReviewDate = (slotISO?: string | null, createdAt?: string | null) => {
  const parsed = parseReviewDate(slotISO) || parseReviewDate(createdAt)
  if (!parsed) return ''
  return parsed.toLocaleDateString('en-AU', { month: 'short', day: 'numeric', year: 'numeric' })
}

const normalizeProviderReply = (reply: any) => {
  if (!reply || typeof reply !== 'object') return null
  const comment = String(reply.comment || reply.replyComment || reply.message || '').trim()
  if (!comment) return null
  const createdAt = String(reply.createdAt || reply.updatedAt || '').trim() || null
  const updatedAt = String(reply.updatedAt || reply.createdAt || '').trim() || null
  const replierName = String(reply.replierName || reply.replier || '').trim() || null
  return {
    comment,
    createdAt,
    updatedAt,
    replierName,
  }
}

const normalizeReviewItem = (review: any) => {
  if (!review || typeof review !== 'object') return review
  return {
    ...review,
    providerReply: normalizeProviderReply(review.providerReply),
  }
}

const sortReviewsByDate = (reviews: any[]) =>
  [...reviews].sort((a, b) => {
    const aDate = parseReviewDate(a?.slotISO) || parseReviewDate(a?.createdAt)
    const bDate = parseReviewDate(b?.slotISO) || parseReviewDate(b?.createdAt)
    const aTime = aDate ? aDate.getTime() : 0
    const bTime = bDate ? bDate.getTime() : 0
    return bTime - aTime
  })

const REVIEWS_PAGE_SIZE = 10
const REVIEWS_FETCH_LIMIT = 50
const MAX_REVIEWS = 500

const providerFirstName = (name?: string | null) => {
  if (!name) return 'their'
  const first = name.trim().split(/\s+/)[0]
  return first || 'their'
}

const directBookingUrl = (item: SearchResult, reason?: string, slotISO?: string) => {
  const stack = guessStack(item)
  const params = new URLSearchParams({
    specialty: 'Direct booking',
    providerId: item.userId || item.slug || 'provider',
    providerSlug: item.slug || (item.name || '').toLowerCase().replace(/[^a-z0-9]+/g, ''),
    providerName: item.name || 'Professional',
  })
  if (item.image) params.set('providerImage', item.image)
  if (reason) params.set('reason', reason)
  if (slotISO) params.set('slot', slotISO)
  return `/results/${stack}?${params.toString()}`
}

export default function ProviderProfilePage() {
  const { providerSlug } = useParams<{ providerSlug: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const stateProvider = (location.state as any)?.provider as SearchResult | undefined
  const initialSlots = Array.isArray(stateProvider?.availabilitySlots) ? stateProvider.availabilitySlots : []
  const [provider, setProvider] = useState<SearchResult | null>(stateProvider ?? null)
  const [providerLoading, setProviderLoading] = useState(!stateProvider)
  const [providerError, setProviderError] = useState<string | null>(null)
  const [slots, setSlots] = useState<string[]>(
    initialSlots
  )
  const [selectedSlot, setSelectedSlot] = useState<string | null>(
    initialSlots.length ? initialSlots[0] : null
  )
  const [availabilityExpanded, setAvailabilityExpanded] = useState(false)
  const [sending, setSending] = useState(false)
  const [reviewsPanel, setReviewsPanel] = useState<{
    provider: SearchResult | null
    reviews: any[]
    summary: any
    loading: boolean
    loadingMore: boolean
    nextCursor: string | null
    error: string | null
  }>({
    provider: null,
    reviews: [],
    summary: null,
    loading: false,
    loadingMore: false,
    nextCursor: null,
    error: null,
  })
  const [reviewsVisibleCount, setReviewsVisibleCount] = useState(REVIEWS_PAGE_SIZE)
  const reviewsScrollRef = useRef<HTMLDivElement | null>(null)
  const reviewsSentinelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    let cancelled = false
    if (stateProvider) {
      setProvider(stateProvider)
      setProviderLoading(false)
      setProviderError(null)
      return () => {
        cancelled = true
      }
    }
    const slug = providerSlug || ''
    if (!slug) {
      setProvider(null)
      setProviderLoading(false)
      setProviderError('Provider not found.')
      return () => {
        cancelled = true
      }
    }
    ;(async () => {
      setProviderLoading(true)
      setProviderError(null)
      try {
        const found = await findProviderBySlug(slug)
        if (!cancelled) {
          setProvider(found)
          if (found) {
            setProviderError(null)
          } else {
            const token = await fetchAuthToken()
            if (!cancelled) {
              setProviderError(
                token ? 'Provider not found.' : 'Provider not found. Sign in to view internal profiles.'
              )
            }
          }
        }
      } catch {
        if (!cancelled) {
          setProvider(null)
          setProviderError('Unable to load provider profile.')
        }
      } finally {
        if (!cancelled) setProviderLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [providerSlug, stateProvider])

  const providerUserId = provider?.userId || ''
  const stack = guessStack(provider || { profession: '' })
  const resolveStackKey = (item: SearchResult | null): ProviderProfession | null => {
    if (!item) return null
    const explicit = item.stack && STACK_API_BASES[item.stack] ? (item.stack as ProviderProfession) : null
    if (explicit) return explicit
    const inferred = guessStack(item)
    return STACK_API_BASES[inferred] ? inferred : null
  }
  const resolveStackCandidates = (item: SearchResult | null): ProviderProfession[] => {
    if (!item) return []
    const out: ProviderProfession[] = []
    const seen = new Set<ProviderProfession>()
    const add = (key: ProviderProfession | null) => {
      if (!key || seen.has(key) || !STACK_API_BASES[key]) return
      seen.add(key)
      out.push(key)
    }
    const explicit = item.stack && STACK_API_BASES[item.stack] ? (item.stack as ProviderProfession) : null
    add(explicit)
    add(guessStack(item))
    ;(Object.keys(STACK_LABELS) as ProviderProfession[]).forEach((key) => add(key))
    return out
  }
  const specialties = provider?.specialties || []
  const profileText = applyProfileDisclaimers(provider?.profile || '', specialties)
  const ratingValue = typeof provider?.rating === 'number' ? provider.rating : null
  const showReviews = (provider?.ratingCount || 0) > 0

  useEffect(() => {
    const nextSlots = Array.isArray(provider?.availabilitySlots) ? provider.availabilitySlots : []
    setSlots(nextSlots)
    setSelectedSlot(nextSlots.length ? nextSlots[0] : null)
  }, [providerUserId, provider?.availabilitySlots])

  useEffect(() => {
    let cancelled = false
    if (slots.length || !providerUserId) return
    ;(async () => {
      try {
        const res = await atlasGeneric.availabilitySlots(stack as any, providerUserId, 30)
        const fetched = Array.isArray(res?.slots) ? res.slots : []
        if (!cancelled && fetched.length) {
          setSlots(fetched)
          setSelectedSlot((prev) => prev || fetched[0])
        }
      } catch {
        // ignore
      }
    })()
    return () => {
      cancelled = true
    }
  }, [stack, providerUserId, slots.length])
  useEffect(() => {
    setAvailabilityExpanded(false)
  }, [providerUserId, slots.length])

  const priceLabel =
    provider?.price && !provider.price.toLowerCase().includes('contact')
      ? `${provider.price} (per 30 minutes)`
      : provider?.price || null
  const providerLocation = provider
    ? buildProviderLocation({
        city: (provider as any)?.city,
        suburb: (provider as any)?.suburb,
        state: (provider as any)?.state,
        region: (provider as any)?.region,
        location: provider.location,
        countryCode: provider.countryCode,
        country: provider.country,
      }).display || provider.location || null
    : null
  const meta = [
    shouldHideLocation(provider) ? null : providerLocation,
    priceLabel,
  ]
    .filter(Boolean)
    .join(' · ')
  const stackLabel = STACK_LABELS[stack] || provider?.profession || 'Professional'
  const nextSlots = Array.isArray(slots)
    ? slots.slice(0, 3)
    : []
  const nextAvailabilityLabel = nextSlots.length
    ? (() => {
        const first = nextSlots[0]
        try {
          const d = new Date(first)
          const now = new Date()
          const isToday = d.toDateString() === now.toDateString()
          const isTomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000).toDateString() === d.toDateString()
          const dayLabel = isToday
            ? 'Today'
            : isTomorrow
              ? 'Tomorrow'
              : d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })
          return dayLabel
        } catch {
          return 'Next available'
        }
      })()
    : null
  const nextSlotOptions = nextSlots
    .map((slot) => {
      try {
        const d = new Date(slot)
        const label = d.toLocaleString('en-AU', {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          hour: 'numeric',
          minute: '2-digit',
          hour12: false,
        })
        return { iso: slot, label }
      } catch {
        return null
      }
    })
    .filter(Boolean) as { iso: string; label: string }[]
  const hasMoreAvailability = Array.isArray(slots) && slots.length > nextSlots.length
  const availabilityDays = useMemo(() => {
    if (!availabilityExpanded || !Array.isArray(slots)) return []
    const parsed = slots
      .map((iso) => {
        try {
          const d = new Date(iso)
          if (Number.isNaN(d.getTime())) return null
          const dayKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
            d.getDate()
          ).padStart(2, '0')}`
          const dayLabel = d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })
          const timeLabel = d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: false })
          return { iso, time: d.getTime(), dayKey, dayLabel, timeLabel }
        } catch {
          return null
        }
      })
      .filter(Boolean) as { iso: string; time: number; dayKey: string; dayLabel: string; timeLabel: string }[]
    parsed.sort((a, b) => a.time - b.time)
    const groups: { key: string; label: string; slots: { iso: string; label: string }[] }[] = []
    const map = new Map<string, { label: string; slots: { iso: string; label: string }[] }>()
    parsed.forEach((slot) => {
      if (!map.has(slot.dayKey)) {
        map.set(slot.dayKey, { label: slot.dayLabel, slots: [] })
        groups.push({ key: slot.dayKey, label: slot.dayLabel, slots: map.get(slot.dayKey)!.slots })
      }
      map.get(slot.dayKey)!.slots.push({ iso: slot.iso, label: slot.timeLabel })
    })
    return groups
  }, [availabilityExpanded, slots])

  const openReviews = async (item: SearchResult) => {
    setReviewsPanel({ provider: item, reviews: [], summary: null, loading: true, loadingMore: false, nextCursor: null, error: null })
    setReviewsVisibleCount(0)
    await fetchAllReviews(item)
  }

  const closeReviews = () => {
    setReviewsPanel({ provider: null, reviews: [], summary: null, loading: false, loadingMore: false, nextCursor: null, error: null })
  }

  useEffect(() => {
    if (reviewsPanel.provider) {
      document.body.classList.add('reviews-no-scroll')
    } else {
      document.body.classList.remove('reviews-no-scroll')
    }
    return () => document.body.classList.remove('reviews-no-scroll')
  }, [reviewsPanel.provider])

  const fetchAllReviews = async (item: SearchResult) => {
    const preferredStack = resolveStackKey(item)
    const stackCandidates = resolveStackCandidates(item)
    if (!stackCandidates.length || !item.userId) {
      setReviewsPanel((prev) => ({
        ...prev,
        loading: false,
        loadingMore: false,
        error: 'Reviews unavailable for this provider.',
      }))
      return
    }
    try {
      let best: { reviews: any[]; summary: any; score: number } | null = null
      let lastError: any = null

      for (const stackKey of stackCandidates) {
        const apiBase = STACK_API_BASES[stackKey]
        if (!apiBase) continue
        try {
          let cursor: string | null = null
          let summary: any = null
          let all: any[] = []
          let pages = 0
          do {
            const qs = new URLSearchParams({ limit: String(REVIEWS_FETCH_LIMIT), ts: Date.now().toString() })
            if (cursor) qs.set('cursor', cursor)
            const res = await fetch(`${apiBase}/reviews/providers/${item.userId}?${qs.toString()}`, { cache: 'no-store' })
            const json = await res.json().catch(() => ({}))
            if (!res.ok) throw new Error(json?.error || 'Failed to load reviews')
            const incoming = Array.isArray(json.reviews) ? json.reviews.map(normalizeReviewItem) : []
            all = all.concat(incoming)
            summary = json.summary || summary
            cursor = json.nextCursor || null
            pages += 1
          } while (cursor && all.length < MAX_REVIEWS && pages < 20)
          const sorted = sortReviewsByDate(all)
          const hasReplies = sorted.some((rev) => Boolean(rev?.providerReply?.comment))
          const ratingCount = Number(summary?.ratingCount || 0)
          const score =
            (hasReplies ? 1000000 : 0) +
            (stackKey === preferredStack ? 10000 : 0) +
            ratingCount * 10 +
            sorted.length
          if (!best || score > best.score) {
            best = { reviews: sorted, summary, score }
          }
          if (hasReplies && sorted.length) break
        } catch (err: any) {
          lastError = err
        }
      }

      if (!best) throw lastError || new Error('Failed to load reviews')

      setReviewsPanel({
        provider: item,
        reviews: best.reviews,
        summary: best.summary,
        loading: false,
        loadingMore: false,
        nextCursor: null,
        error: null,
      })
      setReviewsVisibleCount(Math.min(REVIEWS_PAGE_SIZE, best.reviews.length))
    } catch (e: any) {
      setReviewsPanel((prev) => ({
        ...prev,
        loading: false,
        loadingMore: false,
        error: e?.message || 'Failed to load reviews',
      }))
    }
  }

  const loadMoreReviews = () => {
    if (reviewsVisibleCount >= reviewsPanel.reviews.length) return
    setReviewsVisibleCount((count) => Math.min(count + REVIEWS_PAGE_SIZE, reviewsPanel.reviews.length))
  }

  useEffect(() => {
    if (!reviewsPanel.provider) return
    if (reviewsVisibleCount >= reviewsPanel.reviews.length) return
    const root = reviewsScrollRef.current
    const target = reviewsSentinelRef.current
    if (!root || !target) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMoreReviews()
      },
      { root, rootMargin: '0px 0px 220px 0px', threshold: 0 }
    )
    observer.observe(target)
    return () => observer.disconnect()
  }, [reviewsPanel.provider, reviewsPanel.reviews.length, reviewsVisibleCount, loadMoreReviews])

  if (providerLoading) {
    return (
      <section className="profile-shell container">
        <button className="btn" type="button" onClick={() => navigate('/clients')} style={{ marginBottom: 16 }}>
          <ArrowLeft size={16} aria-hidden /> Back to search
        </button>
        <div className="profile-card">
          <div className="muted">Loading profile…</div>
        </div>
      </section>
    )
  }

  if (!provider) {
    return (
      <section className="profile-shell container">
        <button className="btn" type="button" onClick={() => navigate('/clients')} style={{ marginBottom: 16 }}>
          <ArrowLeft size={16} aria-hidden /> Back to search
        </button>
        <div className="profile-card">
          <div className="banner error">{providerError || 'Provider not found.'}</div>
        </div>
      </section>
    )
  }

  return (
    <section className="profile-shell container">
      <button className="btn" type="button" onClick={() => navigate('/clients')} style={{ marginBottom: 16 }}>
        <ArrowLeft size={16} aria-hidden /> Back to search
      </button>

      <div className="profile-card">
        <div className="profile-card-head">
          <img
            src={provider.image || '/logo.png'}
            alt={provider.name || 'Professional'}
            className="profile-card-photo"
          />
          <div className="profile-card-meta">
            <p className="eyebrow">{stackLabel}</p>
            <h1 className="provider-name-line">
              <span>{provider.name}</span>
              <LicenseVerifiedBadge item={provider} />
            </h1>
            <p className="muted profile-meta-line">{meta || '—'}</p>
            {showReviews ? (
              <div className="reviews-meta">
                <button
                  type="button"
                  className="pill-btn"
                  onClick={() => openReviews(provider)}
                >
                  <span className="value">{ratingValue ? ratingValue.toFixed(1) : 'Reviews'}</span>
                  <span aria-hidden>{renderStars(ratingValue || 5)}</span>
                  <span className="reviews-count">{reviewCountLabel(provider.ratingCount)}</span>
                </button>
              </div>
            ) : null}
          </div>
        </div>

        <div className="profile-card-body">
          {provider.pricingNotes && (
            <div className="banner info" style={{ marginTop: 6 }}>
              {provider.pricingNotes}
            </div>
          )}

          {profileText && (
            <div
              className="profile-bio"
              dangerouslySetInnerHTML={{ __html: renderProfileHtml(profileText) }}
            />
          )}

          {specialties.length > 0 && (
            <div className="profile-specialties">
              <div className="clients-search-tags">
                {specialties.slice(0, 12).map((tag) => (
                  <span key={tag} className="pill pill-muted">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {nextSlotOptions.length > 0 && (
            <div className="profile-availability">
              <div className="availability-head">
                <p className="availability-title">Next available</p>
                {nextAvailabilityLabel ? (
                  <p className="availability-sub">{nextAvailabilityLabel}</p>
                ) : null}
                {hasMoreAvailability ? (
                  <button
                    type="button"
                    className="availability-toggle"
                    onClick={() => setAvailabilityExpanded((open) => !open)}
                    aria-expanded={availabilityExpanded}
                  >
                    {availabilityExpanded ? 'Hide full availability' : 'See full availability'}
                  </button>
                ) : null}
              </div>
              <div className="availability-chips">
                {nextSlotOptions.map((opt) => (
                  <button
                    key={opt.iso}
                    type="button"
                    className={`availability-chip${selectedSlot === opt.iso ? ' is-selected' : ''}`}
                    onClick={() => setSelectedSlot(opt.iso)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {availabilityExpanded ? (
                <div className="profile-availability-calendar" aria-label="Full availability">
                  {availabilityDays.length ? (
                    availabilityDays.map((day) => (
                      <div key={day.key} className="profile-availability-day">
                        <div className="profile-availability-day-title">{day.label}</div>
                        <div className="profile-availability-day-slots">
                          {day.slots.map((slot) => (
                            <button
                              key={slot.iso}
                              type="button"
                              className={`availability-chip${selectedSlot === slot.iso ? ' is-selected' : ''}`}
                              onClick={() => setSelectedSlot(slot.iso)}
                            >
                              {slot.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="muted">No additional times listed.</p>
                  )}
                </div>
              ) : null}
            </div>
          )}

          <div className="profile-actions">
            <button
              type="button"
              className="btn btn-primary cta-badged profile-cta"
              onClick={() => {
                setSending(true)
                navigate(directBookingUrl(provider, undefined, selectedSlot || undefined))
              }}
            >
              <span className="cta-badge">FREE</span>
              {sending ? 'Send Booking Request...' : 'Send Booking Request'}
            </button>
          </div>
        </div>
      </div>

      {reviewsPanel.provider && (
        <div className="reviews-overlay" role="dialog" aria-modal="true" onClick={closeReviews}>
          <div className={`reviews-drawer ${reviewsPanel.loading ? 'is-loading' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="reviews-head">
              <button className="reviews-close" type="button" aria-label="Close reviews" onClick={closeReviews}>
                ✕
              </button>
            </div>
            <div className="reviews-scroll" ref={reviewsScrollRef}>
              <div className="reviews-title">{`What ${providerFirstName(reviewsPanel.provider.name)}'s customers say`}</div>
              <div className="reviews-summary">
                <div className="reviews-brand">
                  <img
                    src={reviewsPanel.provider.image || '/logo.png'}
                    alt={reviewsPanel.provider.name}
                    className="reviews-provider-photo"
                  />
                  <div className="reviews-provider-name">{reviewsPanel.provider.name}</div>
                </div>
                <div className="reviews-avg">
                  <div className="reviews-avg-score">
                    {Number(reviewsPanel.summary?.ratingAverage ?? Number(reviewsPanel.provider.rating || 0)).toFixed(1)}
                  </div>
                  <div className="reviews-avg-stars" aria-hidden="true">
                    {renderStars(reviewsPanel.summary?.ratingAverage ?? reviewsPanel.provider.rating)}
                  </div>
                  <div className="reviews-avg-count">
                    {reviewCountLabel(reviewsPanel.summary?.ratingCount ?? reviewsPanel.provider.ratingCount ?? 0)}
                  </div>
                </div>
                <button
                  className="reviews-cta-primary"
                  type="button"
                  onClick={() => {
                    const url = directBookingUrl(reviewsPanel.provider, undefined, selectedSlot || undefined)
                    navigate(url)
                  }}
                >
                  <span className="cta-badge">FREE</span>
                  Send Booking Request
                </button>
              </div>
              <div className="reviews-body">
                {reviewsPanel.loading && <div className="muted">Loading reviews…</div>}
                {reviewsPanel.error && !reviewsPanel.loading && <div className="banner error">{reviewsPanel.error}</div>}
                {!reviewsPanel.loading && !reviewsPanel.error && !reviewsPanel.reviews.length ? (
                  <div className="muted">No reviews yet.</div>
                ) : null}
                {!reviewsPanel.loading &&
                  !reviewsPanel.error &&
                  reviewsPanel.reviews.slice(0, reviewsVisibleCount).map((rev, idx) => (
                    <div key={rev.reviewId || rev.id || idx} className="review-item">
                      <div className="review-head">
                        <div className="reviewer-badge" aria-hidden="true">
                          {(rev.reviewerFirstName || 'Client').slice(0, 1).toUpperCase()}
                        </div>
                        <div className="reviewer-meta">
                          <strong>{rev.reviewerFirstName || 'Client'}</strong>
                          <span className="hint">
                            {formatReviewDate(rev.slotISO, rev.createdAt)}
                            {rev.source ? (
                              <>
                                {' - '}
                                <em>{rev.source}</em>
                              </>
                            ) : null}
                          </span>
                        </div>
                        <div className="review-rating">{renderStars(rev.rating)}</div>
                      </div>
                      {rev.comment ? <p className="review-comment">{rev.comment}</p> : null}
                      {rev.providerReply?.comment ? (
                        <div className="review-provider-reply">
                          <div className="hint" style={{ marginBottom: 4 }}>
                            {rev.providerReply?.replierName || 'Professional'} replied
                          </div>
                          <p className="review-comment" style={{ marginTop: 0 }}>
                            {rev.providerReply.comment}
                          </p>
                        </div>
                      ) : null}
                    </div>
                  ))}
                {reviewsVisibleCount < reviewsPanel.reviews.length && !reviewsPanel.loading && !reviewsPanel.error ? (
                  <button
                    className="reviews-cta-secondary"
                    type="button"
                    onClick={loadMoreReviews}
                    disabled={reviewsVisibleCount >= reviewsPanel.reviews.length}
                  >
                    Load more reviews
                  </button>
                ) : null}
                <div ref={reviewsSentinelRef} />
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
